import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { ToastGuid } from '../types/toastGuid';

type ToastGuidsResponse = {
  data: ToastGuid[];
  meta?: {
    total: number;
  };
};

type DefaultError = unknown;

export const toastGuidQueryKeys = {
  all: ['toastGuids'] as const,
  lists: () => [...toastGuidQueryKeys.all, 'list'] as const,
  list: (params: Record<string, string>) =>
    [...toastGuidQueryKeys.lists(), params] as const,
  details: () => [...toastGuidQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...toastGuidQueryKeys.details(), id] as const,
};

type UseToastGuidsParams = {
  searchParams?: Record<string, string>;
  enabled?: boolean;
};

export function useToastGuids<TData = ToastGuidsResponse, TError = DefaultError>(
  { searchParams = {}, enabled = true }: UseToastGuidsParams = {},
  options?: Omit<
    UseQueryOptions<ToastGuidsResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = toastGuidQueryKeys.list(searchParams);

  return useQuery<ToastGuidsResponse, TError, TData>({
    queryKey,
    enabled,
    queryFn: async () => service.getToastGuids(searchParams),
    ...(options as any),
  });
}
